import { useGetPaymentTasks } from '@/apis/payment_tasks/useGetPaymentTasks'
import { PaymentTask } from '@/app/apis/payment_tasks/schema'
import PaymentStatusBadge from '@/app/common/components/PaymentStatusBadge'
import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { formatDateToUsersTimezone } from '@/lib/utils'
import { AppRoutePaths } from '@/routing/route_paths'
import { RootState } from '@/storage/store.ts'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const columnHelper = createColumnHelper<PaymentTask>()

const RECORDS_PER_PAGE = 10 as const
export default function PaymentsPage() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const navigate = useNavigate()
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>(
    format(new Date(), 'hh:mm:ss a')
  )

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, refetch } = useGetPaymentTasks({
    page: currentPage,
    perPage: recordsPerPage,
    search: debouncedSearch,
    programId: selectedProgramYear?.id,
  })

  const payments = data?.data || []
  const totalRows = data?.total || 0
  const lastPage = data?.last_page || 1

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
    setCurrentPage(1) // Reset to first page when changing items per page
  }

  const handleRefresh = () => {
    refetch()
    setLastUpdatedAt(format(new Date(), 'hh:mm:ss a'))
  }

  const columns = [
    columnHelper.accessor('payment_id', {
      id: 'payment_id',
      header: () => (
        <>
          <span className="hidden pl-4 sm:block">{'ID'}</span>
          <span className="block pl-4 sm:hidden">{'ID'}</span>
        </>
      ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.payment_id}</span>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{row.student_name.join(', ')}</div>
                <div>{row.payment_type_name}</div>
                <div>{row.provider_name}</div>
                <div>Request #{row.payment_id}</div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor((row) => `${row.applicant_name}`, {
      id: 'applicant_name',
      header: () => 'Applicant Name',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor((row) => `${row.student_name.join(', ')} `, {
      id: 'student_name',
      header: () => 'Student Name',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('payment_type_name', {
      id: 'payment_type_name',
      header: () => 'Type',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('provider_name', {
      id: 'provider_name',
      header: () => 'Provider',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('total_amount', {
      id: 'total_amount',
      header: () => 'Total Amount',
      cell: (info) => <div>{currencyFormatter.format(info.getValue())}</div>,
    }),

    columnHelper.accessor('comments', {
      id: 'comments',
      header: () => 'Comments',
      cell: (info) => (
        <div className="max-w-[5rem] truncate">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('invoice_quote', {
      id: 'invoice_quote',
      header: () => 'Invoice No',
      cell: (info) => (
        <div className="max-w-[5rem] truncate">{info.getValue()}</div>
      ),
    }),

    columnHelper.accessor('payment_status', {
      id: 'payment_status',
      header: () => 'Status',
      cell: (info) => (
        <div>
          <PaymentStatusBadge value={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.accessor('submitted_at', {
      header: () => 'Submitted At',
      cell: (info) => {
        const lastDateTimeStr = info.getValue()
        return (
          <span>
            {lastDateTimeStr !== null
              ? formatDateToUsersTimezone(lastDateTimeStr)
              : ''}
          </span>
        )
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Button
            onClick={() => {
              // Use payment_id from API response when navigating
              navigate(
                AppRoutePaths.payments.details.index.replace(
                  ':paymentId',
                  row.payment_id.toString()
                )
              )
            }}
          >
            Review
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: lastPage,
  })

  return (
    <>
      <section aria-labelledby="payments-list-title">
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Payment Requests
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {`last updated at: ${lastUpdatedAt}`}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="table_search"
                  placeholder="Search Payments"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={isLoading}
                    onClick={handleRefresh}
                  >
                    <CircleArrowsIcon
                      className={cn('text-base', isLoading && 'animate-spin')}
                    />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {`last updated at: ${lastUpdatedAt}`}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={cn(
                          'w px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                          index != 0 &&
                            index != headerGroup.headers.length - 1 &&
                            'hidden md:table-cell',
                          index == headerGroup.headers.length - 1 && 'text-left'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {(function () {
                if (isLoading) {
                  return (
                    <tbody>
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="text-center py-4"
                        >
                          Loading...
                        </td>
                      </tr>
                    </tbody>
                  )
                }

                if (payments.length === 0 && !isLoading) {
                  return (
                    <tbody>
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="text-center py-4"
                        >
                          No payments found
                        </td>
                      </tr>
                    </tbody>
                  )
                }

                return (
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => (
                          <td
                            key={cell.id}
                            className={cn(
                              ' py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                              index != 0 &&
                                index != row.getVisibleCells().length - 1 &&
                                'hidden md:table-cell',
                              index == row.getVisibleCells().length - 1 &&
                                'text-left'
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )
              })()}
            </table>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={totalRows}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}
